// Admin API client.
//
// - Attaches Firebase ID token on every request.
// - On 401: signs the user out and redirects to /login.
// - On 403: surfaces the error so the UI can toast (no redirect).
// - On 501: returns a sentinel so pages can render the "backend not yet
//   implemented" empty state (Phase 1 — most endpoints stub 501).
// - In dev, optionally validates responses against zod schemas from
//   @goodsweat/domain (shared backend contract). Validation failures are
//   warnings, not errors — schemas use `.default(...)` for several
//   fields, which means the post-parse output type may differ from the
//   raw response shape. We trust the backend route to land the contract
//   and only warn when it doesn't.

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';
import type {
  AdminRole,
  AdminUser,
  AuditLogEntry,
  BundleCreateBody,
  BundleRule,
  BundleUpdateBody,
  CatalogProjection,
  Channel,
  Coupon,
  CouponCreateBody,
  CouponUpdateBody,
  CreatorInterestRequest,
  CreatorInterestRequestPriority,
  CreatorInterestRequestStatus,
  Customer,
  Dispute,
  EmailSequence,
  EmailSequenceCreateBody,
  EmailSequenceUpdateBody,
  EmailSend,
  EmailTemplate,
  EmailTemplateCreateBody,
  EmailTemplateUpdateBody,
  Order,
  Payout,
  Product,
  ProductCreateBody,
  ProductUpdateBody,
  Refund,
  RefundReason,
  ResolveCartResult,
  Sku,
  SkuCreateBody,
  SkuUpdateBody,
  Subscription,
  SubscriptionMutationBody,
  Timestamp,
} from '@goodsweat/domain';
import { clearTokenCache, getIdToken, signOut } from './auth';

// ───── Error class ────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True for the "Phase 1 backend stub" empty state. */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

// ───── Axios instance ────────────────────────────────────────────────

export const axiosClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
});

axiosClient.interceptors.request.use(async (config) => {
  const token = await getIdToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

axiosClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<{ code?: string; message?: string }>) => {
    const status = error.response?.status ?? 0;
    const payload = error.response?.data ?? {};
    const code = payload.code ?? error.code ?? 'unknown_error';
    const message = payload.message ?? error.message ?? 'Request failed';

    if (status === 401) {
      // Token rejected — wipe cache, sign out, bounce to login.
      clearTokenCache();
      try {
        await signOut();
      } catch {
        /* ignore */
      }
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/login?reason=session_expired';
      }
    }

    throw new ApiError(status, code, message, payload);
  },
);

// ───── Internal helpers ───────────────────────────────────────────────

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await axiosClient.get<T>(url, { params });
  return res.data;
}

async function patch<T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await axiosClient.patch<T>(url, data, config);
  return res.data;
}

async function post<T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await axiosClient.post<T>(url, data, config);
  return res.data;
}

async function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await axiosClient.delete<T>(url, config);
  return res.data;
}

/** A page of items + an opaque cursor for the next page. */
export interface ListResponse<T> {
  items: T[];
  /** Backend route convention. */
  next_cursor?: string | null;
  /** Legacy field on some routes; prefer `next_cursor`. */
  cursor?: string | null;
}

// ───── Resource methods ──────────────────────────────────────────────

export const api = {
  customers: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<Customer>>('/customers', params);
    },
    get(id: string) {
      return get<CustomerWithRelations>(`/customers/${id}`);
    },
    /**
     * The backend's PATCH allow-list is narrower than CustomerUpdateBody:
     * only `tags`, `notes`, and `email_consent.marketing` are accepted.
     * See admin/backend/src/routes/customers.ts.
     */
    update(
      id: string,
      body: {
        tags?: string[];
        notes?: string;
        email_consent?: { marketing: boolean };
      },
    ) {
      return patch<Customer>(`/customers/${id}`, body);
    },
  },

  orders: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<Order>>('/orders', params);
    },
    get(id: string) {
      return get<OrderWithRelations>(`/orders/${id}`);
    },
    listForCustomer(customerId: string, limit = 20) {
      return get<ListResponse<Order>>('/orders', {
        customer_id: customerId,
        limit,
      });
    },
    addNote(id: string, text: string) {
      return post<OrderNote>(`/orders/${id}/notes`, { text });
    },
    listNotes(id: string) {
      return get<ListResponse<OrderNote>>(`/orders/${id}/notes`);
    },
    /**
     * Issue a refund against the order. The backend writes a
     * `refunds/{re_…}` row with `initiated_by: 'operator'` and
     * `operator_email` set to the current admin's email. `amount_cents`
     * defaults to the remaining refundable balance (`total_cents − refunded_cents`).
     */
    refund(
      id: string,
      body: { amount_cents?: number; reason?: RefundReason; note?: string },
    ) {
      return post<{ ok: true; refund: Refund }>(`/orders/${id}/refund`, body);
    },
    /** Per-order embed of refunds + disputes for the detail panel. */
    listRefunds(id: string) {
      return get<ListResponse<Refund>>(`/orders/${id}/refunds`);
    },
    listDisputes(id: string) {
      return get<ListResponse<Dispute>>(`/orders/${id}/disputes`);
    },
  },

  refunds: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<Refund>>('/refunds', params);
    },
  },

  disputes: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<Dispute>>('/disputes', params);
    },
  },

  payouts: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<Payout>>('/payouts', params);
    },
  },

  /**
   * Reports — backend computes the cross-collection rollups so the UI
   * doesn't have to. See admin/backend/src/routes/reports.ts (built by
   * backend-engineer in parallel).
   */
  reports: {
    summary(params?: { from?: string; until?: string; mode?: string }) {
      return get<ReportSummaryResponse>('/reports/summary', params);
    },
  },

  subscriptions: {
    /**
     * When `include_customer=true` is set, each item carries an optional
     * `_customer` sidecar so the list can render display_name + primary_email
     * without an N+1 join. See `SubscriptionListRow` below.
     */
    list(params?: Record<string, unknown>) {
      return get<ListResponse<SubscriptionListRow>>('/subscriptions', params);
    },
    get(id: string) {
      return get<Subscription & { upcoming_invoice?: UpcomingInvoiceSummary | null }>(
        `/subscriptions/${id}`,
      );
    },
    /** Recent invoices for the detail page's "Invoice history" card. */
    listInvoices(id: string, limit = 20) {
      return get<{ items: InvoiceSummary[] }>(
        `/subscriptions/${id}/invoices`,
        { limit },
      );
    },
    listForCustomer(customerId: string) {
      return get<ListResponse<Subscription>>('/subscriptions', {
        customer_id: customerId,
      });
    },
    mutate(id: string, body: SubscriptionMutationBody) {
      return post<Subscription>(`/subscriptions/${id}`, body);
    },
    // Phase 2 action endpoints. Each round-trips Stripe + Firestore + audit.
    pause(
      id: string,
      behavior: 'mark_uncollectible' | 'keep_as_draft' | 'void' = 'keep_as_draft',
    ) {
      return post<Subscription>(`/subscriptions/${id}/pause`, { behavior });
    },
    resume(id: string) {
      return post<Subscription>(`/subscriptions/${id}/resume`, {});
    },
    skipNext(id: string) {
      return post<Subscription>(`/subscriptions/${id}/skip-next`, {});
    },
    changeCadence(id: string, cadenceId: string) {
      return post<Subscription>(`/subscriptions/${id}/change-cadence`, {
        cadence_id: cadenceId,
      });
    },
    changeSku(id: string, sku: string, qty?: number) {
      return post<Subscription>(`/subscriptions/${id}/change-sku`, { sku, qty });
    },
    applyPromo(id: string, promoCodeId: string) {
      return post<Subscription>(`/subscriptions/${id}/apply-promo`, {
        promo_code_id: promoCodeId,
      });
    },
    removePromo(id: string) {
      return post<Subscription>(`/subscriptions/${id}/remove-promo`, {});
    },
    refundInvoice(
      id: string,
      body: { invoice_id: string; amount?: number; reason?: string },
    ) {
      return post<{
        ok: true;
        refund: { id: string; amount: number; status: string };
      }>(`/subscriptions/${id}/refund-invoice`, body);
    },
    cancel(id: string, opts: { at_period_end: boolean; prorate?: boolean }) {
      return post<Subscription>(`/subscriptions/${id}/cancel`, opts);
    },
  },

  products: {
    list() {
      return get<ListResponse<ProductListRow>>('/products');
    },
    get(slug: string) {
      return get<ProductWithRelations>(`/products/${slug}`);
    },
    create(body: ProductCreateBody) {
      return post<Product>('/products', body);
    },
    update(slug: string, body: ProductUpdateBody) {
      return patch<Product>(`/products/${slug}`, body);
    },
    remove(slug: string) {
      return del<{ ok: boolean }>(`/products/${slug}`);
    },
  },

  skus: {
    list(productSlug?: string) {
      const params = productSlug ? { product_slug: productSlug } : undefined;
      return get<ListResponse<Sku>>('/skus', params);
    },
    get(id: string) {
      return get<Sku>(`/skus/${id}`);
    },
    create(body: SkuCreateBody) {
      return post<Sku>('/skus', body);
    },
    update(id: string, body: SkuUpdateBody) {
      return patch<Sku>(`/skus/${id}`, body);
    },
  },

  bundles: {
    list() {
      return get<ListResponse<BundleRule>>('/bundles');
    },
    get(id: string) {
      return get<BundleRule>(`/bundles/${id}`);
    },
    create(body: BundleCreateBody) {
      return post<BundleRule>('/bundles', body);
    },
    update(id: string, body: BundleUpdateBody) {
      return patch<BundleRule>(`/bundles/${id}`, body);
    },
    remove(id: string) {
      return del<{ ok: boolean }>(`/bundles/${id}`);
    },
    move(id: string, direction: 'up' | 'down') {
      return post<BundleRule>(`/bundles/${id}/move`, { direction });
    },
    preview(body: {
      lines: { sku: string; qty: number; unit_price_cents: number }[];
      channel: Channel;
    }) {
      return post<ResolveCartResult>('/bundles/preview', body);
    },
  },

  coupons: {
    list() {
      return get<ListResponse<Coupon>>('/coupons');
    },
    get(id: string) {
      return get<Coupon>(`/coupons/${id}`);
    },
    create(body: CouponCreateBody) {
      return post<Coupon>('/coupons', body);
    },
    update(
      id: string,
      body: CouponUpdateBody & {
        enabled?: boolean;
        add_promo_code?: string;
        remove_promo_code_id?: string;
      },
    ) {
      return patch<Coupon>(`/coupons/${id}`, body);
    },
    archive(id: string) {
      return axiosClient
        .delete<Coupon>(`/coupons/${id}`)
        .then((r) => r.data);
    },
  },

  influencers: {
    list(params?: Record<string, unknown>) {
      return get<CreatorRequestListResponse>('/influencers', params);
    },
    get(id: string) {
      return get<CreatorRequest>(`/influencers/${id}`);
    },
    update(id: string, body: CreatorRequestUpdateBody) {
      return patch<CreatorRequest>(`/influencers/${id}`, body);
    },
  },

  emailTemplates: {
    list() {
      return get<ListResponse<EmailTemplate>>('/emails/templates');
    },
    get(id: string) {
      return get<EmailTemplate>(`/emails/templates/${id}`);
    },
    create(body: EmailTemplateCreateBody) {
      return post<EmailTemplate>('/emails/templates', body);
    },
    update(id: string, body: EmailTemplateUpdateBody) {
      return patch<EmailTemplate>(`/emails/templates/${id}`, body);
    },
  },

  emailSequences: {
    list() {
      return get<ListResponse<EmailSequence>>('/emails/sequences');
    },
    get(id: string) {
      return get<EmailSequence>(`/emails/sequences/${id}`);
    },
    create(body: EmailSequenceCreateBody) {
      return post<EmailSequence>('/emails/sequences', body);
    },
    update(id: string, body: EmailSequenceUpdateBody) {
      return patch<EmailSequence>(`/emails/sequences/${id}`, body);
    },
  },

  emailSends: {
    list(params?: Record<string, unknown>) {
      return get<ListResponse<EmailSend>>('/emails/sends', params);
    },
  },

  emailBroadcasts: {
    /** Preview audience count + sample for a given template + filter. */
    preview(body: {
      template_id: string;
      audience_filter: {
        channel?: Channel;
        has_marketing_consent?: true;
        email_address_kind?: 'direct';
        tags?: string[];
      };
      scheduled_at?: string;
    }) {
      return post<{
        id: string;
        template_id: string;
        audience_filter: Record<string, unknown>;
        scheduled_at: string | null;
        audience_count: number;
        audience_sample: string[];
      }>('/emails/broadcasts', body);
    },
    /** Fire the broadcast (Phase 3: fan-out, no persisted draft yet). */
    send(
      draftId: string,
      body: {
        template_id: string;
        audience_filter: {
          channel?: Channel;
          has_marketing_consent?: true;
          email_address_kind?: 'direct';
          tags?: string[];
        };
        scheduled_at?: string;
      },
    ) {
      return post<{
        broadcast_id: string | null;
        enqueued: number;
        sent: number;
        skipped: number;
        failed: number;
      }>(`/emails/broadcasts/${draftId}/send`, body);
    },
  },

  emailSequencesActions: {
    /** Fire a test run of a sequence against a sample customer. */
    run(id: string, body: { customer_id: string; extra_vars?: Record<string, unknown> }) {
      return post<{
        results: Array<{ sequence_id: string; enqueued_count: number; send_ids: string[] }>;
      }>(`/emails/sequences/${id}/run`, body);
    },
  },

  audit: {
    /**
     * Read audit_log entries scoped to a single subject. `collection` is the
     * Firestore collection name (`orders`, `coupons`, …); the backend
     * singularizes it to the stored `subject_type` (`order`, `coupon`).
     */
    listFor(collection: string, subjectId: string, limit = 50) {
      return get<ListResponse<AuditLogEntry>>('/audit', {
        ref: `${collection}:${subjectId}`,
        limit,
      });
    },
    /**
     * Top-level audit log feed for the /audit page. Supports actor, action,
     * subject_type, and date-range filters.
     */
    list(params?: Record<string, unknown>) {
      return get<ListResponse<AuditLogEntry>>('/audit', params);
    },
  },

  catalog: {
    preview() {
      return get<CatalogPreviewResponse>('/catalog/preview');
    },
    publish() {
      return post<CatalogPublishResponse>('/catalog/publish', {});
    },
    publicCurrent() {
      return get<CatalogProjection>('/catalog_public');
    },
  },

  settings: {
    listUsers() {
      return get<ListResponse<AdminUser & { pending_uid?: boolean }>>(
        '/settings/users',
      );
    },
    inviteUser(body: { email: string; name: string; role: AdminRole }) {
      return post<AdminUser>('/settings/users', body);
    },
    updateUser(
      uid: string,
      body: { role?: AdminRole; name?: string; disabled?: boolean },
    ) {
      return patch<AdminUser>(`/settings/users/${uid}`, body);
    },
    removeUser(uid: string) {
      return del<{ ok: boolean }>(`/settings/users/${uid}`);
    },
    listChannels() {
      return get<ListResponse<ChannelStatus>>('/settings/channels');
    },

    // ─── API keys + runtime mode ─────────────────────────────────────
    // Backend ownership: admin-backend-engineer. Contract documented in
    // the api-keys backend prompt §4. Responses are metadata only — the
    // raw key values never leave Secret Manager.
    apiKeys: {
      // The backend returns each service's slots as an OBJECT keyed by slot
      // name (`{ secret: {set, masked, updated_at}, webhook: {...} }`). The
      // page consumes them as an ARRAY (`[{ key, is_set, masked_value, ... }]`).
      // Adapter normalizes here so the page stays clean.
      async list(): Promise<ApiKeysListResponse> {
        const raw = await get<{
          services: Array<{
            id: ApiKeyService;
            live: Record<string, { set: boolean; masked: string | null; updated_at: string | null }>;
            test: Record<string, { set: boolean; masked: string | null; updated_at: string | null }>;
          }>;
          current_mode: RuntimeMode;
        }>('/settings/api-keys');
        const toArray = (
          obj: Record<string, { set: boolean; masked: string | null; updated_at: string | null }>,
        ): ApiKeyMeta[] =>
          Object.entries(obj).map(([key, slot]) => ({
            key,
            is_set: slot.set,
            masked_value: slot.masked,
            updated_at: slot.updated_at,
            updated_by_email: null,
          }));
        return {
          services: raw.services.map((s) => ({
            service: s.id,
            live: toArray(s.live),
            test: toArray(s.test),
          })),
          current_mode: raw.current_mode,
        };
      },
      replace(args: {
        service: ApiKeyService;
        mode: RuntimeMode;
        key: string;
        value: string;
      }) {
        const { service, mode, key, value } = args;
        return patch<{ ok: true; updated: ApiKeyMeta }>(
          `/settings/api-keys/${service}/${mode}/${key}`,
          { value },
        );
      },
    },

    runtimeMode: {
      get() {
        return get<RuntimeModeResponse>('/settings/runtime-mode');
      },
      flip(body: { mode: RuntimeMode; confirm_phrase: string }) {
        return post<RuntimeModeResponse>('/settings/runtime-mode', body);
      },
    },

    modeCounts: {
      // Tiny endpoint backing the test-mode banner counters.
      get() {
        return get<ModeCountsResponse>('/settings/mode-counts');
      },
    },
  },

  // Top-level audit reader. `audit.listFor(collection, id)` is the
  // per-subject form already used by detail pages; `audit.list({ref})`
  // is the explicit form used by pages that already know the ref string
  // (e.g. settings/api-keys reading admin_config:runtime entries).
  audit_query: {
    list(params: { ref: string; limit?: number }) {
      return get<ListResponse<AuditLogEntry>>('/audit', params);
    },
  },
};

// ─── API keys + runtime mode types ─────────────────────────────────
// Mirror of the backend contract. Metadata-only — `value` is never
// returned, only a masked preview.

export type RuntimeMode = 'live' | 'test';
export type ApiKeyService = 'stripe' | 'tiktok' | 'postmark';

export interface ApiKeyMeta {
  /** Stable key name within a service, e.g. `secret`, `webhook`, `app_key`. */
  key: string;
  /** True if Secret Manager has a non-empty value. */
  is_set: boolean;
  /** Last 4 chars (or service-appropriate mask). Null when unset. */
  masked_value: string | null;
  /** When the value was last replaced. Null when unset. */
  updated_at: string | null;
  /** Email of the admin who last replaced it. Null when unset. */
  updated_by_email: string | null;
}

export interface ApiKeyServiceMeta {
  service: ApiKeyService;
  live: ApiKeyMeta[];
  test: ApiKeyMeta[];
}

export interface ApiKeysListResponse {
  services: ApiKeyServiceMeta[];
  current_mode: RuntimeMode;
}

export interface RuntimeModeResponse {
  mode: RuntimeMode;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface ModeBucketCounts {
  orders: number;
  customers: number;
  subscriptions: number;
}

export interface ModeCountsResponse {
  live: ModeBucketCounts;
  test: ModeBucketCounts;
  current_mode: RuntimeMode;
}

export interface CreatorRequest
  extends Omit<CreatorInterestRequest, 'schemaVersion'> {
  id: string;
  schemaVersion?: number;
}

export interface CreatorRequestCounts {
  total?: number;
  by_status?: Partial<Record<CreatorInterestRequestStatus, number>>;
  new?: number;
  reviewing?: number;
  sample_approved?: number;
  sample_sent?: number;
  declined?: number;
  archived?: number;
}

export interface CreatorRequestListResponse
  extends ListResponse<CreatorRequest> {
  counts?: CreatorRequestCounts;
}

export interface CreatorRequestUpdateBody {
  status?: CreatorInterestRequestStatus;
  priority?: CreatorInterestRequestPriority;
  tags?: string[];
  internalNotes?: string;
}

/** Customer detail response — backend joins in latest orders + subs. */
export interface CustomerWithRelations extends Customer {
  orders: Order[];
  subscriptions: Subscription[];
  /** Optional — backend embeds when available. */
  refunds?: Refund[];
  disputes?: Dispute[];
}

/**
 * Order detail response — backend embeds per-order refunds + disputes so
 * the modal doesn't fan out a second + third request. Fields are optional
 * for back-compat with pre-rollout responses.
 */
export interface OrderWithRelations extends Order {
  refunds?: Refund[];
  disputes?: Dispute[];
}

/** Top-line metrics for /reports. Computed by the backend across orders/refunds/disputes/subs. */
export interface ReportSummaryResponse {
  range: { from: string; until: string; mode: RuntimeMode };
  revenue: {
    gross_cents: number;
    fee_cents: number;
    refunded_cents: number;
    net_cents: number;
  };
  orders: { count: number; aov_cents: number };
  mrr_cents: number;
  refund_rate: number; // 0..1
  dispute_rate: number; // 0..1
  top_skus: Array<{ sku: string; qty: number; gross_cents: number }>;
}

/** Product detail response — backend joins in SKUs + referencing bundles. */
export interface ProductWithRelations extends Product {
  skus: Sku[];
  bundles: BundleRule[];
}

/** Product list row — backend joins inline SKUs. */
export interface ProductListRow extends Product {
  skus: Sku[];
}

/**
 * Subscription list row — when `include_customer=true` is passed to
 * `GET /api/subscriptions`, the backend embeds a small `_customer` sidecar
 * so the list can render display_name + primary_email without an N+1
 * round-trip to /customers. Absent → fall back to `customer_id`.
 */
export interface SubscriptionListRow extends Subscription {
  _customer?: {
    display_name: string;
    primary_email: string;
  };
}

/**
 * Subscription invoice — the row shape returned by
 * `GET /api/subscriptions/:id/invoices`. Unix-second timestamps (Stripe
 * native) so the UI multiplies by 1000 at the render boundary.
 */
export interface InvoiceSummary {
  id: string; // in_…
  number: string | null; // human invoice number (NULL on drafts)
  status:
    | 'paid'
    | 'open'
    | 'void'
    | 'uncollectible'
    | 'draft'
    | string;
  total_cents: number;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  currency: string;
  period_start: number | null; // unix seconds
  period_end: number | null;
  created: number; // unix seconds
  paid_at: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  attempt_count: number;
}

/**
 * Upcoming invoice sidecar on subscription detail. Stripe's
 * `retrieveUpcomingInvoice` projected to the fields the UI needs.
 */
export interface UpcomingInvoiceSummary {
  amount_due: number;
  amount_paid: number;
  total: number;
  currency: string;
  period_start: number | null;
  period_end: number | null;
  next_payment_attempt: number | null;
}

export interface OrderNote {
  id: string;
  order_id: string;
  text: string;
  author_uid: string;
  author_email: string;
  created_at: string;
}

export interface CatalogPreviewResponse {
  /** Projection that would publish right now. */
  snapshot: CatalogProjection;
  /** Currently-published version, if any. The publish modal diffs against this. */
  current?: CatalogProjection | null;
  /** Summary counters — backend may pre-compute or the UI computes from snapshot/current. */
  summary?: {
    prices_changed?: number;
    bundles_added?: number;
    bundles_removed?: number;
    skus_disabled?: number;
    skus_enabled?: number;
  };
}

export interface CatalogPublishResponse {
  public_url: string | null;
  version_id: string;
  snapshot: CatalogProjection;
}

export interface ChannelStatus {
  id: Channel;
  connected: boolean;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export type AdminApi = typeof api;
